import React, { useEffect, useRef } from 'react';
import { useHistory, useLocation } from 'react-router-dom';
import axios from 'services/axios';

/**
 * Component that checks backend health once on app load.
 * Redirects to /no-connection if backend is unavailable.
 * Does NOT re-run on route change, so navigating to e.g. /recycles won't
 * trigger a redirect before the page can load or send its own requests.
 */
const BackendHealthCheck = ({ children }) => {
  const history = useHistory();
  const location = useLocation();
  const healthCheckDone = useRef(false);

  useEffect(() => {
    // Skip check if already on no-connection page
    if (location.pathname === '/no-connection') {
      return;
    }

    // Check if we're seeing a 502 error page (nginx error)
    // Use only "502 Bad Gateway" (or similar) so we don't false-positive on page content
    // that contains "502" (e.g. recycles page product codes like PRB502)
    const pageText = document.body.innerText || document.body.textContent || '';
    const titleText = document.title || '';
    const is502ErrorPage = pageText.includes('502 Bad Gateway') ||
      titleText.includes('502') ||
      pageText.includes('Bad Gateway');
    if (is502ErrorPage) {
      history.replace({
        pathname: '/no-connection',
        state: { from: { pathname: location.pathname } }
      });
      return;
    }

    // Run the actual backend health check only once per app session
    if (healthCheckDone.current) {
      return;
    }

    healthCheckDone.current = true;

    const checkBackend = async () => {
      try {
        try {
          await axios.get('/health', {
            timeout: 3000,
            validateStatus: (status) => status < 500 // Accept any status < 500
          });
        } catch (healthError) {
          const healthStatus = healthError.response?.status;
          if (healthStatus && healthStatus >= 500) {
            throw healthError;
          }
          await axios.get('/', {
            timeout: 3000,
            validateStatus: (status) => status < 500
          });
        }
      } catch (error) {
        const status = error.response?.status;
        const is502 = status === 502 || error.message?.includes('502') || error.message?.includes('Bad Gateway');
        const isServerError = status && status >= 500;
        const isNetworkError = !error.response && (
          error.code === 'ECONNABORTED' ||
          error.message?.includes('timeout') ||
          error.message?.includes('Network')
        );

        if (is502 || isServerError || isNetworkError) {
          history.replace({
            pathname: '/no-connection',
            state: { from: { pathname: location.pathname } }
          });
        }
      }
    };

    checkBackend();
  }, [history, location]);

  return <>{children}</>;
};

export default BackendHealthCheck;

